import { Eye, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { ListToolbar } from '~/components/layout';
import {
  Card,
  CardBody,
  ConfirmModal,
  DataTable,
  RowActionsMenu,
  type DataTableColumn,
} from '~/components/ui';
import { useDeleteProductMutation } from '~/modules/query/featureFlags';

import { errorText, orDash } from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type { Product } from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

export const ProductsTab = ({ reference }: { reference: Reference }) => {
  const [deleting, setDeleting] = useState<Product | null>(null);
  const [deleteProduct] = useDeleteProductMutation();

  const remove = (product: Product) => {
    deleteProduct(product.key)
      .unwrap()
      .then(() => toast.success('Product deleted'))
      .catch((err) =>
        toast.error(
          errorText(
            err,
            'The product could not be deleted. It may still be used by flags, updates, requests or users.',
          ),
        ),
      );
  };

  const columns: DataTableColumn<Product>[] = [
    {
      id: 'name',
      header: 'Product',
      cell: (p) => (
        <div>
          <p className="font-medium text-gray-800 dark:text-white/90">{p.name}</p>
          <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{p.key}</p>
        </div>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      cell: (p) =>
        p.description ? (
          <span className="block max-w-lg truncate" title={p.description}>
            {p.description}
          </span>
        ) : (
          orDash(null)
        ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (p) => (
        <RowActionsMenu
          testId={`feature-flags-products-actions-${p.key}`}
          items={[
            {
              label: 'Edit',
              testId: 'edit',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: routes.featureFlagsAdminRecord('products', p.key),
            },
            {
              label: 'View requests',
              testId: 'requests',
              icon: <Eye className="size-4" aria-hidden="true" />,
              href: `${routes.featureFlagsSection('requests')}?product=${encodeURIComponent(p.key)}`,
            },
            {
              label: 'Delete',
              testId: 'delete',
              destructive: true,
              icon: <Trash2 className="size-4" aria-hidden="true" />,
              onClick: () => setDeleting(p),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Card>
        <ListToolbar
          title="Products"
          description="Each product owns its flags, product updates, feature requests and known users."
        />
        <CardBody className="p-0">
          <DataTable<Product>
            framed={false}
            testId="feature-flags-products-table"
            loading={reference.pending && reference.products.length === 0}
            emptyMessage="No products yet."
            emptyDescription={
              'Add your first product to start collecting ideas and targeting flags.'
            }
            data={reference.products}
            rowKey={(p) => p.key}
            href={(p) => routes.featureFlagsAdminRecord('products', p.key)}
            rowLabel={(p) => `Open ${p.name}`}
            columns={columns}
          />
        </CardBody>
      </Card>

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete product"
        message={`Delete product '${deleting?.name}'?`}
        confirmLabel="Delete"
        variant="danger"
        dangerNote="The product and its submission rules are removed."
        testId="feature-flags-products-delete"
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove(deleting);
          setDeleting(null);
        }}
      />
    </>
  );
};

export default ProductsTab;
